package com.devi3east.bashbuddy.features.common.util

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.supabase.SupabaseClient
import com.mohamedrejeb.calf.core.LocalPlatformContext
import com.mohamedrejeb.calf.io.readByteArray
import com.mohamedrejeb.calf.picker.FilePickerFileType
import com.mohamedrejeb.calf.picker.FilePickerSelectionMode
import com.mohamedrejeb.calf.picker.rememberFilePickerLauncher
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.storage.UploadData
import io.github.jan.supabase.storage.storage
import io.ktor.utils.io.ByteReadChannel
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock

@Composable
fun FilePicker(
    token: String,
    buttonText: String,
    onFileUploaded: (String) -> Unit,
    onUploadError: (String) -> Unit = {},
    modifier: Modifier = Modifier,
    organizationId: String? = null,
    relatedTitle: String? = null,
    relatedType: String? = null
) {
    val scope = rememberCoroutineScope()
    val context = LocalPlatformContext.current

    val pickerLauncher = rememberFilePickerLauncher(
        type = FilePickerFileType.Image,
        selectionMode = FilePickerSelectionMode.Single,
        onResult = { files ->
            val file = files.firstOrNull() ?: return@rememberFilePickerLauncher

            scope.launch {
                try {
                    if (token.isBlank()) {
                        onUploadError("User not authenticated")
                        return@launch
                    }

                    SupabaseClient.client.auth.importAuthToken(
                        accessToken = token,
                        retrieveUser = true
                    )

                    // ---- Read file bytes ----
                    val bytes = file.readByteArray(context)
                    if (bytes.isEmpty()) {
                        onUploadError("Selected file is empty")
                        return@launch
                    }

                    // ---- Build filename ----
                    val orgPart = organizationId?.trim()?.replace("\\s+".toRegex(), "_")?.lowercase() ?: "org"
                    val titlePart = relatedTitle?.trim()?.replace("\\s+".toRegex(), "_")?.lowercase() ?: "item"
                    val typePart = relatedType?.trim()?.lowercase() ?: "image"
                    val timestamp = Clock.System.now().toEpochMilliseconds()
                    val fileName = "${orgPart}_${titlePart}_${typePart}_${timestamp}.jpg"

                    // ---- Upload ----
                    val uploadResult = SupabaseClient.client.storage
                        .from("event-images")
                        .upload(
                            path = fileName,
                            data = UploadData(
                                stream = ByteReadChannel(bytes),
                                size = bytes.size.toLong()
                            )
                        )

                    // ---- Generate public URL ----
                    val publicUrl = SupabaseClient.client.storage
                        .from("event-images")
                        .publicUrl(fileName)

                    onFileUploaded(publicUrl)

                } catch (e: Exception) {
                    e.printStackTrace()
                    onUploadError("Upload failed: ${e.message}")
                }
            }
        }
    )

    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Button(
            onClick = {
                pickerLauncher.launch()
            },
            shape = RoundedCornerShape(12.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = MaterialTheme.colorScheme.primary,
                contentColor = MaterialTheme.colorScheme.onPrimary
            )
        ) {
            Text(
                text = buttonText,
                style = MaterialTheme.typography.titleMedium.copy(
                    color = MaterialTheme.colorScheme.onPrimary
                )
            )
        }
    }
}